<template>
  <div>
    <div
      v-if="emptyData && !loading"
      class="q-mx-md"
    >
      <info
        type="info"
        hide-text
      >
        {{ $t('insights.chart.noData') }}
      </info>
    </div>
    <div
      v-show="!emptyData || loading"
      ref="insightsChartDiv"
      :class="{ 'export': isExporting }"
      class="insights-chart"
    />
  </div>
</template>

<script>
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4pluginsRegression from '@amcharts/amcharts4/plugins/regression';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import { measurementMetrics, workflowTypes } from 'boot/globals';
import Info from 'components/Info';
import {
  chain, filter, find, forEach, get, has, head, isArray,
  isDate, isEmpty, isNumber, map, meanBy,
  merge, range, size, sumBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { markRaw, toRaw } from 'vue';
import { useTime } from 'src/composables/time';
import Interactions from 'src/mixins/Interactions';
import PdfMakeExportMixin from 'src/mixins/PdfMakeExportMixin';
import Targets from 'src/mixins/Targets';
import { programs } from 'src/store/programs';
import { programsTargets } from 'src/store/programsTargets';
import { workflows } from 'src/store/workflows';
import { formatIntervalInDuration } from 'src/utils/DateUtils';
import { logEvent } from 'src/utils/LogEvent';

import { chartAxisBoundaries } from 'components/Insights/utils/chartAxisBoundaries';
import {
  AXIS_DATE_SUFFIX, axisDateField, axisSpaceData, axisSpacePoint,
} from 'components/Insights/utils/axisSpaceData';
import ChartAggregatorHandler from './utils/chartAggregatorHandler';

am4core.addLicense('CH312684032');

const CHART_RIGHT_PADDING = 20;
const LABEL_MAX_WIDTH = 250;

/** @typedef {'png' | 'pdf' | 'pdfdata' | 'xlsx' | 'print'} ExportFormat */

export default {
  name: 'Chart',

  components: { Info },
  mixins: [PdfMakeExportMixin, Interactions, Targets],

  props: {
    data: {
      type: Array,
      required: true,
    },
    dataSeries: {
      type: Array,
      required: true,
    },
    usePhaseLineIntervals: {
      type: Boolean,
      default: false,
    },
    exportColumns: {
      type: Object,
      required: true,
    },
    exportFormatters: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: 'HiRasmus',
      required: false,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: Object,
      default: null,
    },
    chartSettings: {
      type: Object,
      default: null,
    },
    phaseLines: {
      type: Array,
      required: true,
    },
    splitXAxis: {
      type: Object,
      default: null,
    },
    hideLegend: {
      type: Boolean,
      default: false,
    },
    analysis: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    noAnimation: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['loading', 'refreshed'],

  setup() {
    const {
      formatDate, getDay, getHours, addToDate, currentIANATimezone,
      getLocalToTimezoneOffsetDate, getTimezoneOffsetDateToLocal,
    } = useTime();
    return {
      formatDate,
      getHours,
      getDay,
      addToDate,
      currentIANATimezone,
      getLocalToTimezoneOffsetDate,
      getTimezoneOffsetDateToLocal,
    };
  },

  data() {
    return {
      isExporting: false,

      exportDataSource: null,
      exportDataRows: null,

      dataAnalysisLineTypes: {
        average: {
          label: this.$t('insights.chart.average'),
          includeDataSeriesLabel: true,
          plotAcrossPhaseLines: false,
          getValue: (seriesColumn) => (chain(this.data)
            .map(seriesColumn)
            .reject((v) => (v === null) || (v === undefined))
            .mean()
            .defaultTo(null)
            .value()
          ),
        },
        programMasteryCriteria: {
          label: this.$t('insights.chart.masteryCriteria'),
          includeDataSeriesLabel: false,
          plotAcrossPhaseLines: true,
          getValue: () => {
            const programId = chain(this.data)
              .head()
              .get('programId', null)
              .value();

            const program = this.programs.find((p) => p.id === programId);
            const workflow = this.workflows.find((w) => w.id === program?.workflow);
            const adjustedWorkflowStatuses = merge(
              {},
              workflow?.statuses || {},
              program?.workflowAdjustments || {},
            );

            if (workflow) {
              const workflowTypeId = workflow.type || 'accuracyIncrease';
              const workflowType = workflowTypes[workflowTypeId];
              const measurementMetric = measurementMetrics.find((m) => m.id === workflowType?.metric);
              const value = adjustedWorkflowStatuses?.acquisition?.[workflowType?.workflowProp];

              if (typeof value === 'number') {
                if (typeof measurementMetric?.calcFunc === 'function') {
                  return measurementMetric.calcFunc(value);
                }
                return value;
              }
            }

            return null;
          },
        },
      },

    };
  },

  computed: {
    ...mapState(programs, ['programs']),
    ...mapState(workflows, ['workflows']),

    emptyData() {
      return isEmpty(this.data) && !this.loading;
    }, // emptyData()

    /**
     * Whether to use timezone shifting logic. This is made to be a fallback in case there is some unexpected
     * timezone behaviour. By switching back to device timezone we'll disable all new timezone shifting logic
     */
    plotInUserTimezone() {
      return this.currentIANATimezone !== Intl.DateTimeFormat().resolvedOptions().timeZone;
    }, // plotInUserTimezone()

    /**
     * Whether the export needs rows of its own rather than the ones the chart is plotting.
     *
     * @return {boolean}
     */
    transformsExportData() {
      return this.plotInUserTimezone || Object.keys(this.exportFormatters).length > 0;
    }, // transformsExportData()

    /**
     * Data properties plotted on a date axis, so `axisSpaceData` knows which ones to convert.
     * `date` is always included: the data-analysis series builds its own points from it.
     *
     * @return {string[]}
     */
    dateAxisProperties() {
      const dataProperties = this.dataSeries.reduce((acc, dataSeries) => {
        if (dataSeries.xAxisType !== 'category' && dataSeries.xAxisDataProperty) {
          acc.push(dataSeries.xAxisDataProperty);
        }
        return acc;
      }, []);

      return [...new Set([...dataProperties, 'date'])];
    }, // dateAxisProperties()

    showTrendline() {
      return get(this.analysis, 'trendline');
    }, // showTrendline()

    colorTheme() {
      return this.chartSettings?.theme || 'default';
    }, // colorTheme()

    isBlackAndWhiteTheme() {
      return this.colorTheme === 'bw';
    }, // isBlackAndWhite()

    dataAnalysisLines() {
      if (!this.analysis) {
        return [];
      }
      const visibleAnalysis = Object.keys(this.analysis).filter((key) => this.analysis[key]);
      return chain(this.dataAnalysisLineTypes)
        .pick(visibleAnalysis)
        .values()
        .value();
    }, // dataAnalysisLines()

  },

  watch: {
    dataSeries: {
      immediate: true,
      // todo [vue3-migration]: Added deep for now. Should be tested on working vue3 functionality
      deep: true,
      handler(newDataSeries, prevDataSeries) {
        if ((prevDataSeries === undefined) && isEmpty(newDataSeries)) {
          // Empty dataSeries on first watcher fire. If parent says data is still
          // loading, stay quiet — the watcher will fire again with real data and
          // refreshChart() will run then. Only emit the synthetic "no data" signal
          // when we're sure the chart will never have data (loading already done).
          if (!this.loading) {
            this.$emit('loading', false);
            this.$emit('refreshed', false);
          }
          return;
        }

        this.refreshChart();
      },
    },
    title: {
      immediate: false,
      handler() {
        // Make sure chart refreshes when title changes
        this.refreshChart();
      },
    },

    theme: {
      immediate: true,
      deep: true,
      handler(theme) {
        if (!get(theme, 'colors')) {
          return;
        }

        const colorList = chain(theme).get('colors').map((color) => (am4core.color(color))).value();

        const am4themesLocalTheme = (object) => {
          /* eslint-disable no-param-reassign */
          if (object instanceof am4core.ColorSet) {
            object.list = colorList;
            object.minLightness = 0.2;
            object.maxLightness = 0.7;
            object.reuse = true;
          }

          if (object instanceof am4core.ResizeButton) {
            object.background.cornerRadiusTopLeft = 20;
            object.background.cornerRadiusTopRight = 20;
            object.background.cornerRadiusBottomLeft = 20;
            object.background.cornerRadiusBottomRight = 20;
          }

          if (object instanceof am4core.Tooltip) {
            object.animationDuration = 800;
          }
        };
        /* eslint-enable no-param-reassign */

        am4core.useTheme(am4themesLocalTheme);
      },
    },
  },

  created() {
    /**
     * Chart instance should be defined in created() so that it won't be a reactive object as we don't need that
     *
     * @see https://stackoverflow.com/a/45815401
     */
    this.chart = null;
  },

  beforeUnmount() {
    this.disposeChart();
  },

  methods: {
    ...mapActions(programsTargets, ['getProgramTarget']),

    /**
     * Dispose chart if it's rendered
     */
    disposeChart() {
      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
    }, // disposeChart()

    async getExportedChart(exportFormat = 'png') {
      // This cannot be refactored into an async function because the chart needs to be
      // rendered (validated) before it can be exported and the validated AMCharts function
      // does not return a promise.
      return new Promise((resolve) => {
        if (this.chart === null) {
          resolve(null);
          return;
        }

        const chartReady = async () => {
          this.chart.events.off('validated', chartReady, this);
          await this.$nextTick();

          const imgData = await new Promise((res) => {
            if (this.$q.platform.is.capacitor) {
              // wait graph lines to be rendered
              requestAnimationFrame(async () => {
                res(await this.chart.exporting.getImage(exportFormat));
              });
              return;
            }

            // wait graph lines to be rendered
            setTimeout(async () => {
              res(await this.chart.exporting.getImage(exportFormat));
            }, 1);
          });

          this.isExporting = false;
          resolve(imgData);
        };

        this.chart.events.on('validated', chartReady, this);
        this.isExporting = true;
      });
    }, // getExportedChart()

    prepareChartForExport() {
      // Update legend size before exporting
      // If we modify the legend in the exportstarted event handler it can be cropped
      if (this.chart.legend) {
        this.chart.legend.maxHeight = this.chart.legend.contentHeight;
      }
    }, // prepareChartForExport()

    /**
     * @param {ExportFormat} exportFormat
     */
    async exportChart(exportFormat) {
      if (!this.chart?.exporting) {
        this.notifyWarning(this.$t('insights.chart.export.empty'));
        return;
      }

      if (!this.transformsExportData) {
        const { data } = this.chart.exporting;
        this.formatChartData(data);
      }

      this.prepareChartForExport();

      if (['xlsx', 'pdfdata'].includes(exportFormat)) {
        await this.exportChartWithoutResize(exportFormat);
        return;
      }

      await this.exportChartWithResize(exportFormat);
    }, // exportChart()

    /**
     * Export chart without resizing for (preferred for data-export formats)
     *
     * @param {'xlsx' | 'pdfdata'} exportFormat
     */
    async exportChartWithoutResize(exportFormat) {
      try {
        if (this.$q.platform.is.capacitor) {
          await this.exportChartOnNativeApp(exportFormat);
          return;
        }

        await this.chart.exporting.export(exportFormat);
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err);
        console.error(err);
      }
    }, // exportChartWithoutResize()

    /**
     * Export chart with resizing so that it would look the same regardless of screen size
     *
     * @param {ExportFormat} exportFormat
     */
    async exportChartWithResize(exportFormat) {
      const chartReady = async () => {
        this.chart.events.off('validated', chartReady, this);
        await this.$nextTick();

        try {
          await new Promise((resolve, reject) => {
            if (this.$q.platform.is.capacitor) {
              requestAnimationFrame(() => {
                this.exportChartOnNativeApp(exportFormat)
                  .then(() => resolve())
                  .catch((err) => reject(err));
              });
              return;
            }

            setTimeout(() => {
              this.chart.exporting.export(exportFormat)
                .then(() => resolve())
                .catch((err) => reject(err));
            }, 1);
          });
        } catch (err) {
          this.isExporting = false;
          this.notifyNegative(err.message);
          logEvent(err);
          console.error(err);
        }
      };

      this.chart.events.on('validated', chartReady, this);
      this.isExporting = true;
    }, // exportChartWithResize()

    /**
     * @param {ExportFormat} exportFormat
     */
    async exportChartOnNativeApp(exportFormat) {
      let base64;
      let format = exportFormat;
      const exportOptions = this.chart.exporting.formatOptions.getKey(exportFormat);

      switch (exportFormat) {
        case 'pdf':
          base64 = await this.chart.exporting.getPDF(exportFormat, exportOptions);
          break;
        case 'pdfdata':
          base64 = await this.chart.exporting.getPDF(exportFormat, exportOptions);
          format = 'pdf';
          break;
        case 'png':
          base64 = await this.chart.exporting.getImage(exportFormat, exportOptions);
          break;
        case 'xlsx':
          base64 = await this.chart.exporting.getExcel(exportFormat);
          break;
        case 'print':
        default:
          this.isExporting = false;
          return;
      }

      try {
        await this.downloadFileOnNativeApp(base64, `${this.title}.${format}`);
      } finally {
        this.isExporting = false;
      }
    },

    /**
     * Mutates chart data items formatting its learning rate property
     *
     * @param {Array} data;
     */
    formatChartData(data) {
      if (!data || !isArray(data) || isEmpty(data)) {
        return data;
      }

      return map(data, (item) => {
        /* eslint-disable no-param-reassign */
        if (isDate(get(item, 'targetIntroductionDate')) && isDate(get(item, 'targetMasteryDate'))) {
          item.targetLearningRate = formatIntervalInDuration(
            get(item, 'targetIntroductionDate'),
            get(item, 'targetMasteryDate'),
          );
        } else if (isNumber((get(item, 'targetLearningRate')))) {
          item.targetLearningRate = formatIntervalInDuration(new Date(), new Date(), item.targetLearningRate);
        }
        /* eslint-enable no-param-reassign */

        return item;
      });
    }, // formatChartData()

    /**
     * Into the axis' coordinate space, or through unchanged when the axis is left on the device timezone. Identity
     * and conversion have to be chosen by the same flag as `dateFormatter.utc` — a date converted for an axis that
     * reads device-local fields, or the reverse, is off by the zone offset.
     *
     * @param {Timestamp|Date|string} date
     * @return {Timestamp|Date|string}
     */
    toAxisSpace(date) {
      return this.plotInUserTimezone ? this.getLocalToTimezoneOffsetDate(date, 'utc') : date;
    }, // toAxisSpace()

    /**
     * Inverse of {@link toAxisSpace}, for a date read back off the axis.
     *
     * @param {Date} date
     * @return {Date}
     */
    fromAxisSpace(date) {
      return this.plotInUserTimezone ? this.getTimezoneOffsetDateToLocal(date, 'utc') : date;
    }, // fromAxisSpace()

    /**
     * Property the date axis reads.
     *
     * @param {string} dataProperty
     * @return {string}
     */
    axisDateField(dataProperty) {
      return this.plotInUserTimezone ? axisDateField(dataProperty) : dataProperty;
    }, // axisDateField()

    /**
     * @param {Object[]} data
     * @return {Object[]}
     */
    axisSpaceData(data) {
      return this.plotInUserTimezone
        ? axisSpaceData(data, this.dateAxisProperties, this.toAxisSpace)
        : data;
    }, // axisSpaceData()

    /**
     * @param {Date} date
     * @param {Object} properties
     * @return {Object}
     */
    axisSpacePoint(date, properties) {
      return this.plotInUserTimezone
        ? axisSpacePoint(date, properties, this.toAxisSpace)
        : { date, ...properties };
    }, // axisSpacePoint()

    /**
     * The rows the data exports (`xlsx`, `pdfdata`) are written from, as copies of `chart.data`.
     *
     * Their dates carry the profile timezone in the device-local fields both writers read and neither takes a timezone
     * of its own. That is the opposite space to {@link toAxisSpace}, so the axis-space companions are dropped rather
     * than exported. Copies because `formatChartData` replaces `targetLearningRate`, which a series can be plotting
     * from the live data.
     *
     * @param {Object[]} data - `chart.data`
     * @return {Object[]}
     */
    getExportData(data) {
      if (!this.transformsExportData || !Array.isArray(data) || !data.length) {
        return data;
      }

      // `getExcel` re-reads `exporting.data` once per row and this rebuilds the whole set on every
      // read, so without the cache an export is quadratic
      if (toRaw(this.exportDataSource) === toRaw(data)) {
        return this.exportDataRows;
      }

      const exportRows = this.formatChartData(data.map((dataItem) => ({ ...dataItem })));

      this.exportDataSource = data;
      // Raw: nothing renders these and amCharts reads every property of every row
      this.exportDataRows = markRaw(exportRows.map((dataItem) => {
        const exportRow = {};

        Object.entries(dataItem).forEach(([dataProperty, value]) => {
          if (dataProperty.endsWith(AXIS_DATE_SUFFIX)) {
            return;
          }

          // Before the conversion, so a formatter is still handed the instant rather than a shifted date
          const formatter = this.exportFormatters[dataProperty];
          const exportValue = typeof formatter === 'function' ? formatter(value) : value;

          exportRow[dataProperty] = this.plotInUserTimezone && (exportValue instanceof Date)
            ? this.getLocalToTimezoneOffsetDate(exportValue, 'system')
            : exportValue;
        });

        return exportRow;
      }));

      return this.exportDataRows;
    }, // getExportData()

    transformDataToScatterplot(data, dataSeries) {
      const aggregator = dataSeries.chartAggregator || dataSeries.aggregator;

      return chain(data)
        .map((dataItem) => {
          const weekDayLabels = this.$tm('common.weekdays');

          let dataItemDate = get(dataItem, dataSeries.xAxisDataProperty, null);

          dataItemDate = get(dataItem, 'date', null);
          if (!dataItemDate) {
            return;
          }

          const weekDay = this.getDay(dataItemDate);
          const weekDayLabel = get(weekDayLabels, weekDay);
          const hours = this.getHours(dataItemDate);
          const hoursLabel = `${hours}:00`;
          return {
            ...dataItem,
            weekDay,
            weekDayLabel,
            hours,
            hoursLabel,
            _heatMapValue: dataItem[dataSeries.seriesColumn],
          };
        })
        .groupBy((dataItem) => ((dataItem.weekDay + 1) * 10 + (dataItem.hours + 1)))
        .map((dataItems) => {
          let retVal;
          switch (aggregator) {
            case 'low':
              retVal = chain(dataItems).minBy('_heatMapValue').get('_heatMapValue').value();
              break;
            case 'high':
              retVal = chain(dataItems).maxBy('_heatMapValue').get('_heatMapValue').value();
              break;
            case 'sum':
              retVal = sumBy(dataItems, '_heatMapValue');
              break;
            case 'average':
            default:
              retVal = meanBy(dataItems, '_heatMapValue');
          }

          return {
            ...head(dataItems),
            _heatMapValue: retVal,
          };
        })
        .value();
    }, // transformDataToScatterplot()

    refreshToScatterplot(dataSeries) {
      this.$emit('loading', true);
      if (!this.noAnimation) {
        am4core.useTheme(am4themesAnimated);
      }
      const chart = am4core.create(this.$refs.insightsChartDiv, am4charts.XYChart);
      chart.hiddenState.properties.opacity = 1; // Prevent fade-in

      chart.data = this.transformDataToScatterplot(this.data, dataSeries);

      chart.maskBullets = false;

      const xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      const yAxis = chart.yAxes.push(new am4charts.CategoryAxis());

      const weekDayLabels = this.$tm('common.weekdays');
      const minWeekDay = chain(chart.data).minBy('weekDay').get('weekDay').value();
      const maxWeekDay = chain(chart.data).maxBy('weekDay').get('weekDay').value();
      const minHours = chain(chart.data).minBy('hours').get('hours').value();
      const maxHours = chain(chart.data).maxBy('hours').get('hours').value();
      const xAxisData = chain(range(minWeekDay, maxWeekDay + 1))
        .map((weekDay) => ({
          weekDayLabel: get(weekDayLabels, weekDay),
        }))
        .value();
      xAxis.data = xAxisData;
      const yAxisData = chain(range(minHours, maxHours + 1))
        .map((hours) => ({
          hoursLabel: `${hours}:00`,
        }))
        .value();
      yAxis.data = yAxisData;

      xAxis.dataFields.category = 'weekDayLabel';
      yAxis.dataFields.category = 'hoursLabel';

      xAxis.renderer.grid.template.disabled = true;
      xAxis.renderer.minGridDistance = 40;

      yAxis.renderer.grid.template.disabled = true;
      yAxis.renderer.inversed = true;
      yAxis.renderer.minGridDistance = 30;

      const series = chart.series.push(new am4charts.ColumnSeries());
      series.name = get(dataSeries, 'label', '');
      series.dataFields.categoryX = 'weekDayLabel';
      series.dataFields.categoryY = 'hoursLabel';
      series.dataFields.value = '_heatMapValue';
      series.sequencedInterpolation = true;
      if (!this.noAnimation) {
        series.defaultState.transitionDuration = 3000;
      }

      const bgColor = new am4core.InterfaceColorSet().getFor('background');

      const columnTemplate = series.columns.template;
      columnTemplate.strokeWidth = 1;
      columnTemplate.strokeOpacity = 0.2;
      columnTemplate.stroke = bgColor;

      switch (dataSeries.axis) {
        case 'percentage':
          columnTemplate.tooltipText = '{weekDayLabel} {hoursLabel}\n{name}: {value.formatNumber("#.%")}';
          break;
        case 'duration':
          columnTemplate.tooltipText = '{weekDayLabel} {hoursLabel}\n{name}: {value.formatDuration("mm:ss")}';
          break;
        default:
          columnTemplate.tooltipText = '{weekDayLabel} {hoursLabel}\n{name}: {value}';
      }
      columnTemplate.width = am4core.percent(100);
      columnTemplate.height = am4core.percent(100);

      series.heatRules.push({
        target: columnTemplate,
        property: 'fill',
        min: am4core.color(bgColor),
        minValue: 0,
        max: chart.colors.getIndex(0),
      });
      // heat legend
      const heatLegend = chart.bottomAxesContainer.createChild(am4charts.HeatLegend);
      heatLegend.width = am4core.percent(100);
      heatLegend.series = series;
      heatLegend.valueAxis.renderer.labels.template.fontSize = 9;
      heatLegend.valueAxis.renderer.labels.template.fontSize = 9;
      heatLegend.valueAxis.renderer.minGridDistance = 30;

      chart.exporting.title = this.title;
      chart.exporting.filePrefix = this.title;

      chart.exporting.formatOptions.getKey('pdf').addURL = false;
      chart.exporting.formatOptions.getKey('pdfdata').addURL = false;

      // This chart sets no `dataFields`, so amCharts exports every property of a row, raw `date` included
      chart.exporting.adapter.add('data', ({ data }) => ({ data: this.getExportData(data) }));

      const title = chart.titles.create();
      title.text = this.title;
      title.fontSize = 16;
      title.marginBottom = 20;
      title.disabled = !this.showTitle;

      chart.exporting.events.on('exportstarted', (evt) => {
        if ((evt.format === 'png') && (!this.showTitle)) {
          title.disabled = false;
        }
      });

      chart.exporting.events.on('exportfinished', () => {
        this.isExporting = false;
        if (!this.showTitle) {
          title.disabled = true;
        }
      });

      if (!this.noAnimation) {
        am4core.unuseTheme(am4themesAnimated);
      }

      this.$nextTick(() => {
        this.chart = chart;
      });

      chart.events.on('ready', () => {
        if (this.noAnimation) {
          // Transition ended isn't guaranteed to fire after ready when there is no animation
          this.$emit('refreshed', true);
        } else {
          chart.events.on('transitionended', () => {
            this.$emit('refreshed', true);
          });
        }
      });
    }, // refreshToScatterplot()

    getThemeBullet(series, seriesIndex) {
      const themeBullets = get(this.theme, 'bullets', []);
      let bulletType = 'Circle';
      let bulletDirection = 'up';
      let bulletFill = '#FFF';
      let bulletStroke = null;

      if (size(themeBullets) > 0) {
        bulletType = get(themeBullets, [seriesIndex % size(themeBullets), 'type'], 'Circle');
        bulletDirection = get(themeBullets, [seriesIndex % size(themeBullets), 'direction'], 'up');
        bulletFill = get(themeBullets, [seriesIndex % size(themeBullets), 'fill'], '#FFF');
        bulletStroke = get(themeBullets, [seriesIndex % size(themeBullets), 'stroke'], null);
      }

      let bullet;
      let bulletChild;

      switch (bulletType) {
        case 'Triangle':
          bullet = series.bullets.push(new am4charts.Bullet());
          bulletChild = bullet.createChild(am4core.Triangle);
          bulletChild.horizontalCenter = 'middle';
          bulletChild.verticalCenter = 'middle';
          bulletChild.direction = bulletDirection;
          bulletChild.width = 10;
          bulletChild.height = 10;
          break;
        case 'Rectangle':
          bullet = series.bullets.push(new am4charts.Bullet());
          bulletChild = bullet.createChild(am4core.Rectangle);
          bulletChild.horizontalCenter = 'middle';
          bulletChild.verticalCenter = 'middle';
          bulletChild.strokeWidth = 2;
          bulletChild.width = 10;
          bulletChild.height = 10;
          break;
        case 'Circle':
        default:
          bullet = series.bullets.push(new am4charts.CircleBullet());
          bulletChild = bullet.circle;
          bulletChild.strokeWidth = 2;
          bulletChild.radius = 4;
      }

      if (bulletStroke) {
        bulletChild.stroke = am4core.color(bulletStroke);
      }

      if (bulletFill) {
        bulletChild.fill = am4core.color(bulletFill);
      }

      bullet.adapter.add('dy', (dy, target) => {
        const { measuredWidth, measuredHeight } = get(this.chart, 'plotContainer', {});
        const { pixelX, pixelY } = target;

        // eslint-disable-next-line no-param-reassign
        target.visible = !(pixelX < 0 || pixelX > measuredWidth || pixelY < 0 || pixelY > measuredHeight);

        return dy;
      });

      return bullet;
    }, // getThemeBullet()

    linkPhaseChangeSeries(chart, series, dataSeries) {
      series.hiddenInLegend = dataSeries.phaseLineIdx > 0;
      if (this.usePhaseLineIntervals) {
        if (dataSeries.phaseLineIdx > 0) {
          series.seriesIdx = dataSeries.seriesIdx;
        } else {
          series.events.on('hidden', () => {
            chain(chart.series.values)
              .filter({ seriesIdx: dataSeries.seriesIdx })
              .forEach((s) => (s.hide()))
              .value();
          });

          series.events.on('shown', () => {
            chain(chart.series.values)
              .filter({ seriesIdx: dataSeries.seriesIdx })
              .forEach((s) => (s.show()))
              .value();
          });
        }
      }
    },

    setPhaseLineMaxWidth(chart, label) {
      label.events?.on('validated', () => {
        label.maxWidth = chart.axisBulletsContainer?.pixelHeight || LABEL_MAX_WIDTH;
      });
    },

    setRightmostPhaseLinePadding(chart, label, phaseLineTimestamp) {
      label.events?.on('sizechanged', () => {
        const lastChartDate = chart.data?.at(-1)?.date;
        const phaseLineDate = phaseLineTimestamp?.toDate();
        if (lastChartDate && phaseLineDate && (lastChartDate < phaseLineDate)) {
          chart.paddingRight = label.measuredWidth ? CHART_RIGHT_PADDING + label.measuredWidth : CHART_RIGHT_PADDING;
        }
      });
    },

    getAnalysisLineFormattedValue(axisType, value) {
      let tooltipValue;
      let seriesNameValue;

      switch (axisType) {
        case 'percentage':
          tooltipValue = '{value.formatNumber("#.#%")}';
          seriesNameValue = Number.isInteger(value * 100)
            ? `${value * 100}%`
            : `${(value * 100).toFixed(1)}%`;
          break;
        case 'duration':
          tooltipValue = '{value.formatDuration("mm\'min\' ss\'sec\'")}';
          seriesNameValue = this.formatDuration(value);
          break;
        default:
          tooltipValue = Math.round(value * 100) / 100;
          seriesNameValue = tooltipValue;
      }

      return {
        tooltipValue,
        seriesNameValue,
      };
    }, // getAnalysisLineFormattedValue

    formatDuration(seconds) {
      const totalSeconds = Math.floor(seconds);
      const minutes = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      const paddedMinutes = String(minutes).padStart(2, '0');
      const paddedSeconds = String(secs).padStart(2, '0');

      return `${paddedMinutes}min ${paddedSeconds}sec`;
    }, // formatDuration

    getMiddleDate(startDateStr, endDateStr) {
      const startDate = (new Date(startDateStr)).getTime();
      const endDate = (new Date(endDateStr)).getTime();

      if (Number.isNaN(startDate) || Number.isNaN(endDate)) {
        throw new Error('Invalid date format');
      }

      const difference = endDate - startDate;
      const halfway = difference / 2;
      const middleDate = new Date(startDate + halfway);
      return middleDate;
    }, // getMiddleDate

    getMiddleCategory(startCategoryStr, endCategoryStr) {
      const startCategory = Number(startCategoryStr.split(' ')[1]);
      const endCategory = Number(endCategoryStr.split(' ')[1]);

      const difference = endCategory - startCategory;
      const halfway = Math.round(difference / 2);
      const middle = startCategory + halfway;
      const middleCategory = this.data[middle - 1]?.sessionDate;

      return middleCategory || startCategoryStr;
    }, // getMiddleCategory

    calculateRangeWidthInPixels(axisType, axis, start, end) {
      let startCoordinate;
      let endCoordinate;

      if (axisType === 'category') {
        const startPixelPosition = axis.categoryToPosition(start);
        const endPixelPosition = axis.categoryToPosition(end);

        startCoordinate = axis.pixelWidth * startPixelPosition;
        endCoordinate = axis.pixelWidth * endPixelPosition;
      }

      if (axisType === 'date') {
        const startPixelPosition = axis.dateToPosition(this.toAxisSpace(start));
        const endPixelPosition = axis.dateToPosition(this.toAxisSpace(end));

        startCoordinate = axis.axisFullLength * startPixelPosition;
        endCoordinate = axis.axisFullLength * endPixelPosition;
      }

      return endCoordinate - startCoordinate;
    }, // calculateRangeWidthInPixels

    checkLabelsOverlapX(label1, label2) {
      const x1 = label1.label.pixelX;
      const width1 = label1.label.measuredWidth;

      const x2 = label2.label.pixelX;
      const width2 = label2.label.measuredWidth;

      return (x1 < x2 + width2) && (x1 + width1 > x2);
    },

    calculateOptimalStep(totalDays, availableChartWidth, labelWidth) {
      const maxLabels = Math.floor(availableChartWidth / labelWidth);
      return Math.ceil(totalDays / maxLabels);
    },

    refreshChart() {
      this.disposeChart();

      if (isEmpty(this.dataSeries) || isEmpty(this.data)) {
        this.$emit('loading', false);
        this.$emit('refreshed', false);
        return;
      }

      const firstDataSeries = head(this.dataSeries);

      if (get(firstDataSeries, 'chartType') === 'scatterplot') {
        this.refreshToScatterplot(firstDataSeries);
        this.$emit('loading', false);
        this.$emit('refreshed', false);
        return;
      }

      this.$emit('loading', true);

      // Add extra height for status phase line labels above the chart
      const hasStatusPhaseLines = this.phaseLines.some((pl) => pl.isStatusTransition);
      const statusLabelHeight = hasStatusPhaseLines ? 24 : 0;

      this.$refs.insightsChartDiv.style.height = `${330 + statusLabelHeight}px`;

      if (!this.noAnimation) {
        am4core.useTheme(am4themesAnimated);
      }
      const chart = am4core.create(this.$refs.insightsChartDiv, am4charts.XYChart);
      chart.hiddenState.properties.opacity = 1; // Prevent fade-in
      const chartData = this.axisSpaceData(this.data);
      chart.data = chartData;

      chart.paddingRight = CHART_RIGHT_PADDING;
      if (hasStatusPhaseLines) {
        chart.paddingTop = statusLabelHeight;
      }
      chart.maskBullets = false;

      // Set up X Axis
      const dataSeriesMapX = chain(this.dataSeries)
        .map((dataSeries) => ({
          xAxisType: dataSeries.xAxisType,
          xAxisDataProperty: dataSeries.xAxisDataProperty,
          xAxisCategoryLabel: dataSeries.xAxisCategoryLabel,
          xAxisTimeUnit: dataSeries.xAxisTimeUnit,
        }))
        .uniqBy('xAxisType')
        .defaults([{ xAxisType: 'date', xAxisTimeUnit: 'day' }])
        .map((dataSeriesXAxis) => {
          let xAxis;

          switch (dataSeriesXAxis.xAxisType) {
            case 'category': {
              xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
              xAxis.dataFields.category = dataSeriesXAxis.xAxisCategoryLabel;
              xAxis.renderer.cellStartLocation = 0.1;
              xAxis.renderer.cellEndLocation = 0.9;
              xAxis.renderer.minGridDistance = 20;

              xAxis.renderer.grid.template.location = 0;
              xAxis.renderer.labels.template.truncate = true;
              // Allow duplicate labels separately on the X-axis.
              // To be unique, labels must end with " [{something unique}]"
              xAxis.renderer.labels.template.adapter.add('textOutput', (text) => {
                if (!text) {
                  return '';
                }
                return text.replace(/ \[[^\]]*]$/, '');
              });

              let xAxisLabels = [];

              xAxis.events.on('sizechanged', (ev) => {
                if (!this.splitXAxis) {
                  xAxisLabels = [];
                  xAxis.renderer.labels.values.forEach((value) => {
                    xAxisLabels.push({ label: value });
                  });
                }
                if (xAxisLabels.length) {
                  const axis = ev.target;
                  const availableChartWidth = axis.pixelWidth;
                  const dateLabelWidth = xAxisLabels[0].label.measuredWidth;

                  const isLabelsOverlap = xAxisLabels.some((dateRange, i, dateRanges) => {
                    if (i < dateRanges.length - 1 && dateRange.label.opacity !== 0) {
                      return this.checkLabelsOverlapX(dateRange, dateRanges[i + 1]);
                    }
                    return false;
                  });

                  const updateLabelStyle = (label, overlap, cellWidth) => {
                    label.rotation = overlap ? -45 : 0;
                    const horizontalCenterDefault = this.splitXAxis ? 'left' : 'middle';
                    label.horizontalCenter = overlap ? 'right' : horizontalCenterDefault;
                    label.verticalCenter = 'top';
                    if (!this.splitXAxis) {
                      label.maxWidth = cellWidth > 100 ? cellWidth : 100;
                      label.tooltipText = label.text;
                    }
                  };

                  const updateLabelVisibility = (label, show) => {
                    if (show) {
                      label.show();
                    } else {
                      label.hide();
                    }
                  };

                  const { pixelWidth, endIndex, startIndex } = ev.target;
                  const cellWidth = pixelWidth / (endIndex - startIndex);

                  if (isLabelsOverlap) {
                    const step = this.calculateOptimalStep(xAxisLabels.length, availableChartWidth, dateLabelWidth);

                    xAxisLabels.forEach((dateLabel, index) => {
                      updateLabelStyle(dateLabel.label, true, cellWidth);
                      if (this.splitXAxis) {
                        updateLabelVisibility(dateLabel, (index === 0 || index % step === 0));
                      }
                    });
                  } else {
                    xAxisLabels.forEach((dateLabel) => updateLabelStyle(dateLabel.label, false, cellWidth));
                  }
                }
              });

              if (this.splitXAxis !== null && !this.chartSettings?.xDisableGridLines) {
                xAxis.renderer.labels.template.fontSize = 11;
                xAxis.adapter.add('getTooltipText', (text, target) => {
                  const targetDate = this.splitXAxis.format(target.tooltipDataItem.dataContext);
                  return `${text}, ${targetDate}`;
                });

                const splittedData = chain(this.data)
                  .map(this.splitXAxis.field)
                  .uniq()
                  .value();
                forEach(splittedData, (splitDataItem, idx) => {
                  const splitByRange = xAxis.axisRanges.create();
                  xAxisLabels.push(splitByRange);
                  const firstDataItem = chain(this.data)
                    .filter({ [this.splitXAxis.field]: splitDataItem })
                    .head()
                    .value();
                  const lastDataItem = chain(this.data)
                    .filter({ [this.splitXAxis.field]: splitDataItem })
                    .last()
                    .value();
                  splitByRange.category = get(firstDataItem, dataSeriesXAxis.xAxisCategoryLabel);
                  splitByRange.endCategory = get(lastDataItem, dataSeriesXAxis.xAxisCategoryLabel);
                  splitByRange.locations = {
                    category: 0,
                    endCategory: 1,
                  };
                  // Alternative formatting: fill the range with a alternate color
                  // splitByRange.axisFill.fill = am4core.color('#396478');
                  // if (idx % 2 === 0) {
                  splitByRange.axisFill.fillOpacity = 0.00;
                  // } else {
                  // splitByRange.axisFill.fillOpacity = 0.03;
                  // }

                  splitByRange.label.text = this.splitXAxis.format(firstDataItem);
                  splitByRange.label.paddingTop = 30;
                  splitByRange.label.paddingLeft = 5;
                  splitByRange.label.location = 0;
                  splitByRange.label.horizontalCenter = 'left';
                  splitByRange.label.fontWeight = 'bold';
                  splitByRange.label.fontSize = 11;
                  splitByRange.label.truncate = false;

                  // Configure grid line for the range
                  splitByRange.grid.stroke = am4core.color('#808080'); // Gray color for the line
                  splitByRange.grid.strokeWidth = 2; // 2px thickness
                  splitByRange.grid.location = 0; // Start at the beginning of the range
                  splitByRange.grid.strokeOpacity = 0.2; // Make sure the line is fully visible
                });
              }

              const phaseLinesGroupedByCategory = this.phaseLines.reduce((acc, phaseLine) => {
                if (!acc[phaseLine.category]) {
                  acc[phaseLine.category] = [];
                }
                acc[phaseLine.category].push(phaseLine);
                return acc;
              }, {});

              Object.keys(phaseLinesGroupedByCategory).forEach((category) => {
                const phaseLines = phaseLinesGroupedByCategory[category];
                const firstGroupPhaseLine = phaseLines[0];
                const hasStatusTransition = phaseLines.some((pl) => pl.isStatusTransition);

                const phaseRange = xAxis.axisRanges.create();
                phaseRange.category = category;

                if (hasStatusTransition) {
                  // Status transition lines: solid line, no label (label is placed as a centered heading)
                  phaseRange.grid.stroke = am4core.color('black');
                  phaseRange.grid.strokeOpacity = 0.7;
                  phaseRange.grid.strokeWidth = 1.5;
                } else {
                  const combinedLabel = phaseLines.map((phaseLine) => phaseLine.label).join(', ');

                  if (firstGroupPhaseLine.isAfterData) {
                    phaseRange.label.location = 0;
                    phaseRange.locations.category = 1;
                    phaseRange.label.horizontalCenter = 'right';
                  } else {
                    phaseRange.label.location = 0;
                    phaseRange.label.horizontalCenter = 'left';
                  }

                  phaseRange.grid.stroke = am4core.color('black');
                  phaseRange.grid.strokeOpacity = 0.6;
                  phaseRange.grid.strokeWidth = 2;
                  phaseRange.grid.strokeDasharray = '5,2';
                  phaseRange.label.text = combinedLabel;
                  phaseRange.label.inside = true;
                  phaseRange.label.valign = 'top';
                  phaseRange.label.verticalCenter = 'bottom';
                  phaseRange.label.rotation = 90;
                  phaseRange.label.wrap = true;
                  phaseRange.label.truncate = true;
                  this.setPhaseLineMaxWidth(chart, phaseRange.label);
                  this.setRightmostPhaseLinePadding(chart, phaseRange.label, firstGroupPhaseLine.startTimestamp);
                }
              });

              // Add horizontal centered labels above the chart for status transition sections (category axis)
              const statusCategoryPhaseLines = this.phaseLines.filter((pl) => pl.isStatusTransition);
              if (statusCategoryPhaseLines.length > 0 && this.data.length > 0) {
                const firstCategory = this.data[0].sessionDate;
                const lastCategory = this.data[this.data.length - 1].sessionDate;
                // Only render the leading "initial status" section when we have a
                // label for it (see the note in the date-axis branch below).
                const initialLabel = statusCategoryPhaseLines[0].initialStatusLabel;
                const transitionCategories = statusCategoryPhaseLines.map((pl) => pl.category);
                const categoryBoundaries = initialLabel
                  ? [firstCategory, ...transitionCategories, lastCategory]
                  : [...transitionCategories, lastCategory];
                const categoryLabels = initialLabel
                  ? [initialLabel, ...statusCategoryPhaseLines.map((pl) => pl.label)]
                  : statusCategoryPhaseLines.map((pl) => pl.label);

                categoryLabels.forEach((label, idx) => {
                  const sectionRange = xAxis.axisRanges.create();
                  sectionRange.category = categoryBoundaries[idx];
                  sectionRange.endCategory = categoryBoundaries[idx + 1];
                  // Status dividers are drawn at the left edge (location 0) of the
                  // transition category, so each section must end at the divider rather
                  // than the cell's right edge — otherwise the centered label sits half a
                  // cell too far right. Only the final section runs to the axis end.
                  sectionRange.locations = {
                    category: 0,
                    endCategory: idx === categoryLabels.length - 1 ? 1 : 0,
                  };
                  sectionRange.grid.strokeOpacity = 0;
                  sectionRange.label.text = label;
                  sectionRange.label.inside = true;
                  sectionRange.label.valign = 'top';
                  sectionRange.label.location = 0.5;
                  sectionRange.label.horizontalCenter = 'middle';
                  sectionRange.label.verticalCenter = 'bottom';
                  sectionRange.label.dy = -35;
                  sectionRange.label.fontSize = 14;
                });
              }

              break;
            }
            case 'date':
            default: {
              xAxis = chart.xAxes.push(new am4charts.DateAxis());
              if (this.plotInUserTimezone) {
                // Dates reaching this axis are in UTC wall clock (`axisSpaceData`), so grouping, grid boundaries
                // and labels have to read UTC fields. A formatter of its own keeps that off the rest of the chart.
                xAxis.dateFormatter = new am4core.DateFormatter();
                xAxis.dateFormatter.utc = true;
              }
              if (dataSeriesXAxis.xAxisTimeUnit !== null) {
                xAxis.groupData = true;
                xAxis.groupInterval = { timeUnit: dataSeriesXAxis.xAxisTimeUnit, count: 1 };

                if (dataSeriesXAxis.xAxisTimeUnit === 'week') {
                  xAxis.adapter.add('getTooltipText', (text, target) => {
                    // `tooltipDate` comes back out of the axis, so it is in axis space
                    const startDate = this.fromAxisSpace(target?.tooltipDate);
                    const endDate = this.addToDate(startDate, { days: 6 });

                    const startStr = this.formatDate(startDate, 'MMM d');
                    const endStr = this.formatDate(endDate, 'MMM d');

                    return `${startStr} - ${endStr}`;
                  });
                }
              }

              forEach(this.phaseLines, (phaseLine) => {
                const phaseRange = xAxis.axisRanges.create();
                phaseRange.date = this.toAxisSpace(phaseLine.startTimestamp.toDate());
                if (phaseLine.isStatusTransition) {
                  // Status transition lines: solid line, no label (label is placed as a centered heading)
                  phaseRange.grid.stroke = am4core.color('black');
                  phaseRange.grid.strokeOpacity = 0.7;
                  phaseRange.grid.strokeWidth = 1.5;
                } else {
                  phaseRange.grid.stroke = am4core.color('black');
                  phaseRange.grid.strokeOpacity = 0.6;
                  phaseRange.grid.strokeWidth = 2;
                  phaseRange.grid.strokeDasharray = '5,2';
                  phaseRange.label.text = phaseLine.label;
                  phaseRange.label.inside = true;
                  phaseRange.label.valign = 'top';
                  phaseRange.label.horizontalCenter = 'left';
                  phaseRange.label.verticalCenter = 'bottom';
                  phaseRange.label.rotation = 90;
                  phaseRange.label.wrap = true;
                  this.setPhaseLineMaxWidth(chart, phaseRange.label);
                  this.setRightmostPhaseLinePadding(chart, phaseRange.label, phaseLine.startTimestamp);
                }
              });

              // Add horizontal centered labels above the chart for status transition sections
              const statusPhaseLines = this.phaseLines.filter((pl) => pl.isStatusTransition);
              if (statusPhaseLines.length > 0) {
                const firstDataDate = get(this.data, [0, 'date']);
                const lastDataDate = get(this.data, [this.data.length - 1, 'date']);
                if (firstDataDate && lastDataDate) {
                  // Only render the leading "initial status" section when we have a
                  // label for it. If the initial section was empty (initialStatusLabel
                  // cleared upstream in selectedPhaseLines), prepending it here would
                  // fall back to the first transition's own label and render a duplicate
                  // header (e.g. two "Active" headers when a program had no data while
                  // in its baseline status).
                  const initialLabel = statusPhaseLines[0].initialStatusLabel;
                  const transitionDates = statusPhaseLines.map((pl) => pl.startTimestamp.toDate());
                  const boundaries = initialLabel
                    ? [firstDataDate, ...transitionDates, lastDataDate]
                    : [...transitionDates, lastDataDate];
                  const labels = initialLabel
                    ? [initialLabel, ...statusPhaseLines.map((pl) => pl.label)]
                    : statusPhaseLines.map((pl) => pl.label);

                  labels.forEach((label, idx) => {
                    const sectionRange = xAxis.axisRanges.create();
                    sectionRange.date = boundaries[idx];
                    sectionRange.endDate = boundaries[idx + 1];
                    sectionRange.grid.strokeOpacity = 0;
                    sectionRange.label.text = label;
                    sectionRange.label.inside = true;
                    sectionRange.label.valign = 'top';
                    sectionRange.label.location = 0.5;
                    sectionRange.label.horizontalCenter = 'middle';
                    sectionRange.label.verticalCenter = 'bottom';
                    sectionRange.label.dy = -35;
                    sectionRange.label.fontSize = 14;
                  });
                }
              }
              break;
            }
          }

          if (get(this.chartSettings, 'xDisableGridLines', false)) {
            xAxis.renderer.grid.template.opacity = 0;

            forEach(xAxis.axisRanges.values, (phaseRange) => {
              // eslint-disable-next-line no-param-reassign
              phaseRange.grid.opacity = 1;
            });
          }

          if (get(this.chartSettings, 'xAxisLabel', 'auto') === 'custom') {
            xAxis.title.text = get(this.chartSettings, 'xAxisLabelValue', '');
          }

          return xAxis;
        })
        .value();
      // Set up the data series
      const dataSeriesMap = chain(this.dataSeries)
        .map('axis')
        .uniq()
        .defaults(['number'])
        .invert()
        .mapValues((v, dataSeriesAxis) => {
          let yAxis;

          switch (dataSeriesAxis) {
            case 'category': {
              yAxis = chart.yAxes.push(new am4charts.CategoryAxis());
              const dataSeries = find(this.dataSeries, { axis: dataSeriesAxis });
              yAxis.dataFields.category = get(dataSeries, 'axisCategoryLabel');
              yAxis.renderer.labels.template.fontSize = 11;
              yAxis.renderer.minGridDistance = 20;
              const axisSortBy = get(dataSeries, 'axisCategorySortBy');

              if (axisSortBy) {
                yAxis.data = chain(this.data)
                  .uniqBy(axisSortBy)
                  .sortBy(axisSortBy)
                  .value();
                const axisCategorySortOrder = get(dataSeries, 'axisCategorySortOrder', 'asc');
                if (axisCategorySortOrder === 'desc') {
                  yAxis.data = yAxis.data.reverse();
                }
              }
              break;
            }
            case 'percentage':
              yAxis = chart.yAxes.push(new am4charts.ValueAxis());
              yAxis.max = 1.01;
              yAxis.min = 0;
              yAxis.strictMinMax = true;
              yAxis.renderer.maxLabelPosition = 1;
              yAxis.renderer.minLabelPosition = 0;

              yAxis.numberFormatter = new am4core.NumberFormatter();
              yAxis.numberFormatter.numberFormat = '#.%';
              yAxis.extraTooltipPrecision = 1;
              break;
            case 'duration':
              yAxis = chart.yAxes.push(new am4charts.DurationAxis());
              yAxis.title.text = this.$t('insights.chart.durationTitle').toString();
              yAxis.baseUnit = 'second';
              yAxis.min = 0;
              yAxis.durationFormatter.durationFormat = "mm'min' ss'sec'";
              break;
            case 'frequencyMinute':
              yAxis = chart.yAxes.push(new am4charts.ValueAxis());
              yAxis.title.text = this.$t('insights.chart.frequencyMinuteTitle').toString();
              yAxis.adjustLabelPrecision = false;
              yAxis.min = 0;
              yAxis.extraTooltipPrecision = 1;
              break;
            case 'frequencyHour':
              yAxis = chart.yAxes.push(new am4charts.ValueAxis());
              yAxis.title.text = this.$t('insights.chart.frequencyHourTitle').toString();
              yAxis.adjustLabelPrecision = false;
              yAxis.min = 0;
              yAxis.extraTooltipPrecision = 1;
              break;
            case 'frequencyDay':
              yAxis = chart.yAxes.push(new am4charts.ValueAxis());
              yAxis.title.text = this.$t('insights.chart.frequencyDayTitle').toString();
              yAxis.adjustLabelPrecision = false;
              yAxis.min = 0;
              break;
            default: // number
              yAxis = chart.yAxes.push(new am4charts.ValueAxis());
              yAxis.extraTooltipPrecision = 2;
              yAxis.min = 0;

              if (this.dataSeries.some((ds) => ds.dataSeriesPreset === 'spAverageRating')) {
                yAxis.numberFormatter = new am4core.NumberFormatter();
                yAxis.numberFormatter.numberFormat = '#,##0.00';
              }
          }

          if (get(this.chartSettings, 'yAxisLabel', 'auto') === 'custom') {
            yAxis.title.text = get(this.chartSettings, 'yAxisLabelValue', '');
          }

          if (get(this.chartSettings, 'yAxisMin', 'auto') === 'custom') {
            yAxis.strictMinMax = true;

            if (dataSeriesAxis === 'percentage') {
              yAxis.min = get(this.chartSettings, 'yAxisMinValue', 0) / 100;
            } else {
              yAxis.min = get(this.chartSettings, 'yAxisMinValue', 0);
            }
          }

          if (get(this.chartSettings, 'yAxisMax', 'auto') === 'custom' && get(this.chartSettings, 'yAxisMaxValue')) {
            yAxis.strictMinMax = true;

            if (dataSeriesAxis === 'percentage') {
              yAxis.max = this.chartSettings.yAxisMaxValue / 100;
            } else {
              yAxis.max = this.chartSettings.yAxisMaxValue;
            }
          }

          if (dataSeriesAxis === 'number' && get(this.chartSettings, 'yStrictMinMax', false)) {
            chartAxisBoundaries(yAxis, this.chartSettings);
          }

          if (get(this.chartSettings, 'yDisableGridLines', false)) {
            yAxis.renderer.grid.template.disabled = true;
          }

          return yAxis;
        })
        .value();

      const cursorSnapSeries = [];
      forEach(this.dataSeries, (dataSeries, dataSeriesIndex) => {
        let series;

        switch (dataSeries.chartType) {
          case 'column':
          case 'stacked': {
            series = chart.series.push(new am4charts.ColumnSeries());
            series.stacked = (dataSeries.chartType === 'stacked');

            if (has(dataSeries, 'color') && !this.isBlackAndWhiteTheme) {
              series.columns.template.stroke = am4core.color(dataSeries.color);
              series.columns.template.fill = am4core.color(dataSeries.color);
            } else if (has(dataSeries, 'seriesIdx')) {
              const colors = this.theme?.colors;
              if (colors) {
                series.columns.template.stroke = am4core.color(colors[dataSeries.seriesIdx % colors.length]);
                series.columns.template.fill = am4core.color(colors[dataSeries.seriesIdx % colors.length]);
              }
            }

            this.linkPhaseChangeSeries(chart, series, dataSeries);
            break;
          }
          case 'scatterchart': {
            const seriesData = filter(chartData, (dataItem) => has(dataItem, dataSeries.seriesColumn));
            if (!seriesData.length) {
              return;
            }
            series = chart.series.push(new am4charts.LineSeries());

            let plotColor = null;
            if (has(dataSeries, 'color') && !this.isBlackAndWhiteTheme) {
              plotColor = am4core.color(dataSeries.color);
            } else if (has(dataSeries, 'seriesIdx')) {
              const colors = this.theme?.colors;
              if (colors) {
                plotColor = am4core.color(colors[dataSeries.seriesIdx % colors.length]);
              }
            }
            const plotBullet = series.bullets.push(new am4charts.CircleBullet());
            plotBullet.circle.strokeWidth = 2;
            plotBullet.circle.radius = 4;
            if (plotColor) {
              plotBullet.circle.fill = plotColor;
              plotBullet.circle.stroke = plotColor.lighten(-0.1);
            }
            series.strokeOpacity = 0;

            series.dataFields.categoryY = dataSeries.axisCategoryLabel;
            series.dataFields.value = dataSeries.seriesColumn;
            series.data = seriesData;

            break;
          }
          case 'line':
          case 'logarithmic':
          default: {
            series = chart.series.push(new am4charts.LineSeries());
            series.minBulletDistance = 15;
            // Make bullets grow on hover
            const bullet = this.getThemeBullet(series, has(dataSeries, 'seriesIdx')
              ? dataSeries.seriesIdx : dataSeriesIndex);
            const bullethover = bullet.states.create('hover');
            bullethover.properties.scale = 1.3;

            if (has(dataSeries, 'color') && !this.isBlackAndWhiteTheme) {
              series.stroke = am4core.color(dataSeries.color);
            } else if (has(dataSeries, 'seriesIdx')) {
              const colors = this.theme?.colors;
              if (colors) {
                series.stroke = am4core.color(colors[dataSeries.seriesIdx % colors.length]);
              }
            }

            this.linkPhaseChangeSeries(chart, series, dataSeries);
          }
        }

        series.name = get(dataSeries, 'label', '');

        series.connect = true;
        series.autoGapCount = 10000000;

        let trendlineSeries;

        if (this.showTrendline) {
          trendlineSeries = chart.series.push(new am4charts.LineSeries());
          trendlineSeries.dataFields.valueY = dataSeries.seriesColumn;
          trendlineSeries.strokeOpacity = 1;
          trendlineSeries.stroke = series.stroke;
          trendlineSeries.strokeWidth = 2;
          trendlineSeries.simplify = true;
          trendlineSeries.strokeDasharray = '3,3';
          trendlineSeries.name = `${get(dataSeries, 'label', '')} (${this.$t('insights.chart.trend')})`;
          // Hide duplicate trendline legend entries for phase intervals > 0
          // and link them to the phase show/hide mechanism
          if (has(dataSeries, 'phaseLineIdx') && dataSeries.phaseLineIdx > 0) {
            trendlineSeries.hiddenInLegend = true;
          }

          // Link all trendlines to phase show/hide mechanism
          if (has(dataSeries, 'seriesIdx')) {
            trendlineSeries.seriesIdx = dataSeries.seriesIdx;
          }
        }

        // Use data analysis lines as a guide if there are no phase lines.
        // If phase lines are used we must create data analysis lines as an axis range of the series, see below
        if (this.dataAnalysisLines.length > 0) {
          forEach(
            this.dataAnalysisLines.filter(
              (dataAnalysisLine) => (!this.usePhaseLineIntervals || dataAnalysisLine.plotAcrossPhaseLines),
            ),
            async (dataAnalysisLine) => {
              const analysisValue = await dataAnalysisLine.getValue(dataSeries.seriesColumn);
              if (analysisValue !== null) {
                const analysisRange = series.yAxis.axisRanges.create();
                analysisRange.value = analysisValue;

                const { tooltipValue } = this.getAnalysisLineFormattedValue(dataSeries.axis, analysisValue);

                analysisRange.grid.stroke = series.stroke;
                analysisRange.grid.strokeWidth = 2;
                analysisRange.grid.strokeOpacity = 1;
                analysisRange.grid.strokeDasharray = '1,1';
                analysisRange.grid.above = true;
                analysisRange.label.inside = true;
                if (dataAnalysisLine.includeDataSeriesLabel) {
                  analysisRange.label.text = `${get(dataSeries, 'label', '')} (${dataAnalysisLine.label})`;
                } else {
                  analysisRange.label.text = dataAnalysisLine.label;
                }
                analysisRange.label.fill = analysisRange.grid.stroke;
                // Always render the label above the line for consistency with the
                // per-section average labels (see LabelBullet dy: -15 below).
                analysisRange.label.verticalCenter = 'bottom';
                analysisRange.label.hideOversized = false;
                const isLabelOverflow = (chart.innerHeight * (1 - analysisRange.value))
                  < analysisRange.label.measuredHeight;

                analysisRange.axisFill.tooltip = new am4core.Tooltip();
                analysisRange.axisFill.tooltip.ignoreBounds = isLabelOverflow;
                analysisRange.axisFill.tooltipText = `${dataAnalysisLine.label}: ${tooltipValue}`;
                analysisRange.axisFill.interactionsEnabled = true;
                analysisRange.axisFill.isMeasured = true;
                analysisRange.axisFill.tooltipPosition = 'pointer';
              }
            },
          );
        }

        switch (dataSeries.xAxisType) {
          case 'category':
            series.dataFields.categoryX = dataSeries.xAxisCategoryLabel;
            if (this.showTrendline) {
              trendlineSeries.dataFields.categoryX = dataSeries.xAxisCategoryLabel;
            }
            break;
          case 'date':
          default:
            series.dataFields.dateX = this.axisDateField(dataSeries.xAxisDataProperty);
            if (this.showTrendline) {
              trendlineSeries.dataFields.dateX = this.axisDateField(dataSeries.xAxisDataProperty);
            }
            break;
        }

        series.dataFields.valueY = dataSeries.seriesColumn;

        // If specific aggregator is used for charts use that - otherwise use series default
        const chartAggregatorHandler = new ChartAggregatorHandler();
        chartAggregatorHandler.handle(dataSeries.chartAggregator || dataSeries.aggregator, series, dataSeries);

        series.strokeWidth = 3;
        series.yAxis = get(dataSeriesMap, get(dataSeries, 'axis', dataSeries.number));

        series.yAxis.logarithmic = (dataSeries.chartType === 'logarithmic');

        // We must unset the default 0 minimum for yAxis in logarithmic chart
        // because logarithmic scale mathematically breaks on log(0)
        // instead we set it to undefined to let AMCharts auto-calculate a safe minimum
        if (series.yAxis.logarithmic) {
          series.yAxis.min = undefined;
        }

        if (this.showTrendline) {
          trendlineSeries.plugins.push(new am4pluginsRegression.Regression());
        }

        cursorSnapSeries.push(series);
        series.zIndex = 1;
      });

      // If phase lines are used we must create data analysis lines as an axis range of the series
      // to be able to set the start and end points on the X-axis

      if (this.dataAnalysisLines.length > 0) {
        const phaseLinesPoints = this.dataSeries[0].xAxisType === 'category'
          ? this.phaseLines.map((phaseLine) => phaseLine.category)
          : this.phaseLines.map((phaseLine) => phaseLine.startTimestamp.toDate());

        const breakPointsForAnalysisLines = this.dataSeries[0].xAxisType === 'category'
          ? [this.data[0].sessionDate, ...phaseLinesPoints, this.data[this.data.length - 1].sessionDate]
          : [this.data[0].date, ...phaseLinesPoints, this.data[this.data.length - 1].date];

        const analysisScope = [];
        const lastSectionIdx = breakPointsForAnalysisLines.length - 2;
        for (let i = 0; i < breakPointsForAnalysisLines.length - 1; i += 1) {
          analysisScope.push({
            start: breakPointsForAnalysisLines[i],
            end: breakPointsForAnalysisLines[i + 1],
            isLastSection: i === lastSectionIdx,
          });
        }

        forEach(
          this.dataAnalysisLines.filter(
            (dataAnalysisLine) => (this.usePhaseLineIntervals && !dataAnalysisLine.plotAcrossPhaseLines),
          ),
          (dataAnalysisLine) => {
            const analysisLines = analysisScope.map((startEndObj, index) => {
              const avrData = {
                ...startEndObj,
                seriesColumn: this.dataSeries[index].seriesColumn,
                label: this.dataSeries[index].label,
                axis: this.dataSeries[index].axis,
                xAxisType: this.dataSeries[index].xAxisType,
                isLastSection: startEndObj.isLastSection,
              };

              if (Object.hasOwn(this.dataSeries[index], 'seriesIdx')) {
                avrData.seriesIdx = this.dataSeries[index].seriesIdx;
              }

              if (this.dataSeries[index].color) {
                avrData.color = this.dataSeries[index].color;
              }

              return avrData;
            });

            analysisLines.forEach(async (analysisLine) => {
              const value = await dataAnalysisLine.getValue(analysisLine.seriesColumn);

              const analysisSeries = chart.series.push(new am4charts.LineSeries());

              // In the analysisSeries.data array, we should provide the data to create the series.
              // This includes the mean value for the Y-axis and the property from the first and
              // last elements of the this.data array for the X-axis.
              // Depending on the XAxisType, we will use either sessionDate or the date property.
              // Additionally, we need to add an intermediate point in the data array so that we can
              // set a label in the middle of the line as a LabelBullet.

              const middleCategory = analysisLine.xAxisType === 'category'
                ? this.getMiddleCategory(analysisLine.start, analysisLine.end)
                : null;

              if (analysisLine.xAxisType === 'category') {
                analysisSeries.data = [
                  { sessionDate: this.data[0].sessionDate, value },
                  {
                    sessionDate: middleCategory,
                    value,
                    hideBullet: false,
                    bulletText: `${analysisLine.label}\n(${dataAnalysisLine.label})`,
                  },
                  { sessionDate: this.data[this.data.length - 1].sessionDate, value },
                ];
                analysisSeries.dataFields.categoryX = 'sessionDate';
              }

              if (analysisLine.xAxisType === 'date') {
                analysisSeries.data = [
                  this.axisSpacePoint(this.data[0].date, { value }),
                  this.axisSpacePoint(this.getMiddleDate(analysisLine.start, analysisLine.end), {
                    value,
                    hideBullet: false,
                    bulletText: `${analysisLine.label} (${dataAnalysisLine.label})`,
                  }),
                  this.axisSpacePoint(this.data[this.data.length - 1].date, { value }),
                ];
                analysisSeries.dataFields.dateX = this.axisDateField('date');
              }

              const { tooltipValue, seriesNameValue } = this.getAnalysisLineFormattedValue(analysisLine.axis, value);

              analysisSeries.dataFields.valueY = 'value';
              analysisSeries.stroke = am4core.color('#ffffff');
              analysisSeries.interactionsEnabled = true;
              analysisSeries.isMeasured = true;
              analysisSeries.name = `${analysisLine.label} (${dataAnalysisLine.label}): [bold]${seriesNameValue}`;

              const [DateAxis] = dataSeriesMapX;

              // Create an axis range
              const analysisRange = DateAxis.createSeriesRange(analysisSeries);
              if (analysisLine.xAxisType === 'date') {
                analysisRange.date = this.toAxisSpace(analysisLine.start);
                analysisRange.endDate = this.toAxisSpace(analysisLine.end);
              }

              if (analysisLine.xAxisType === 'category') {
                analysisRange.category = analysisLine.start;
                analysisRange.endCategory = analysisLine.end;
                // Phase-line dividers sit at the left edge (location 0) of the
                // section-end category, so each section must end there too —
                // otherwise the average line overshoots into the next section.
                // Only the final section runs to the right edge of its last category.
                analysisRange.locations = {
                  category: 0,
                  endCategory: analysisLine.isLastSection ? 1 : 0,
                };
              }

              if (analysisLine.color && !this.isBlackAndWhiteTheme) {
                analysisRange.contents.stroke = am4core.color(analysisLine.color);
              } else if (Object.hasOwn(analysisLine, 'seriesIdx')) {
                const colors = this.theme?.colors;
                if (colors) {
                  analysisRange.contents.stroke = am4core.color(colors[analysisLine.seriesIdx % colors.length]);
                }
              }

              analysisRange.contents.strokeWidth = 2;
              analysisRange.contents.strokeOpacity = 1;
              analysisRange.contents.strokeDasharray = '1,1';

              chart.events.on('ready', () => {
                const rangeWidthInPixels = this.calculateRangeWidthInPixels(
                  analysisLine.xAxisType,
                  DateAxis,
                  analysisLine.start,
                  analysisLine.end,
                );

                const bullet = analysisSeries.bullets.push(new am4charts.LabelBullet());
                bullet.disabled = true;
                bullet.propertyFields.disabled = 'hideBullet';

                bullet.label.text = '{bulletText}';
                bullet.label.fill = analysisRange.contents.stroke;
                bullet.label.textAlign = 'middle';
                bullet.label.dy = -15;
                bullet.label.maxWidth = 200;
                bullet.label.truncate = false;
                bullet.label.hideOversized = false;
                bullet.label.wrap = true;

                if (rangeWidthInPixels <= 150) {
                  bullet.label.maxWidth = 100;
                  bullet.label.truncate = false;
                  bullet.label.wrap = true;
                }

                // The bullet anchors to a real data point (rounded mid-category on
                // category axes), but the label should sit at the section's visual
                // midpoint, not at that data point. Compute the offset in pixels from
                // the bullet's anchor to the true midpoint and shift the label via dx.
                if (analysisLine.xAxisType === 'category' && middleCategory) {
                  const startPos = DateAxis.categoryToPosition(analysisLine.start, 0);
                  const endLoc = analysisLine.isLastSection ? 1 : 0;
                  const endPos = DateAxis.categoryToPosition(analysisLine.end, endLoc);
                  const bulletPos = DateAxis.categoryToPosition(middleCategory, 0.5);
                  const sectionMidPos = (startPos + endPos) / 2;
                  bullet.label.dx = (sectionMidPos - bulletPos) * DateAxis.pixelWidth;
                }
              });

              analysisSeries.tooltip.getFillFromObject = false;
              analysisSeries.tooltip.background.fill = am4core.color('#000000');

              if (analysisLine.xAxisType === 'date') {
                analysisSeries.adapter.add('tooltipText', (tooltipText, target) => {
                  const currentPoint = target.tooltipDataItem.dataContext?.date.getTime();
                  if (currentPoint >= analysisLine.start && currentPoint <= analysisLine.end) {
                    return `${dataAnalysisLine.label}: ${tooltipValue}`;
                  }
                  return '';
                });
              }

              if (analysisLine.xAxisType === 'category') {
                analysisSeries.adapter.add('tooltipText', (tooltipText, target) => {
                  const currentPoint = target.tooltipDataItem.dataContext?.sessionDate.split(' ')[1];
                  if (currentPoint >= analysisLine.start.split(' ')[1]
                    && currentPoint <= analysisLine.end.split(' ')[1]) {
                    return `${dataAnalysisLine.label}: ${tooltipValue}`;
                  }
                  return '';
                });
              }

              analysisRange.contents.tooltip.dy = -5;
              analysisRange.contents.tooltipPosition = 'pointer';

              cursorSnapSeries.push(analysisSeries);
            });
          },
        );
      }

      if ((this.dataSeries.length > 0) && !this.hideLegend) {
        chart.legend = new am4charts.Legend();
        chart.legend.maxHeight = 100;
        chart.legend.scrollable = true;
      }

      // Mouse cursor
      chart.cursor = new am4charts.XYCursor();
      if (get(firstDataSeries, 'chartType') !== 'scatterchart') {
        // Amcharts does not support snapping to multiple items in the same category
        chart.cursor.snapToSeries = cursorSnapSeries;
      }

      chart.exporting.dataFields = this.exportColumns;
      chart.exporting.title = this.title;
      chart.exporting.useRetina = true;
      chart.exporting.useSimplifiedExport = false;
      chart.exporting.filePrefix = this.title;

      chart.exporting.formatOptions.getKey('pdf').addURL = false;
      chart.exporting.formatOptions.getKey('pdf').minWidth = 1600;
      chart.exporting.formatOptions.getKey('pdf').maxWidth = 1600;
      chart.exporting.formatOptions.getKey('png').minWidth = 1600;
      chart.exporting.formatOptions.getKey('png').maxWidth = 1600;
      chart.exporting.formatOptions.getKey('pdfdata').addURL = false;
      chart.exporting.formatOptions.getKey('pdfdata').fontSize = 10;

      chart.exporting.adapter.add('data', ({ data }) => ({ data: this.getExportData(data) }));

      const title = chart.titles.create();
      title.text = this.title;
      title.fontSize = 20;
      title.marginBottom = 20;
      title.disabled = !this.showTitle;

      chart.exporting.events.on('exportstarted', (evt) => {
        if ((evt.format === 'png') && (!this.showTitle)) {
          title.disabled = false;
          title.parent.invalidate();
        }
      });

      chart.exporting.events.on('exportfinished', () => {
        this.isExporting = false;
        if (!this.showTitle) {
          title.disabled = true;
        }

        if (chart.legend) {
          chart.legend.maxHeight = 100;
        }
      });

      // Add title to validated sprites
      chart.exporting.validateSprites.push(title);
      chart.exporting.validateSprites.push(title.parent);

      if (!this.noAnimation) {
        am4core.unuseTheme(am4themesAnimated);
      }

      this.$nextTick(() => {
        this.chart = chart;
      });

      this.$emit('loading', false);
      chart.events.on('ready', () => {
        if (this.noAnimation) {
          // Transition ended isn't guaranteed to fire after ready when there is no animation
          this.$emit('refreshed', true);
        } else {
          chart.events.on('transitionended', () => {
            this.$emit('refreshed', true);
          });
        }
      });
    }, // refreshChart
  },
};
</script>

<style>

.insights-chart {
  width: 100%;
  height: 330px;
}

.insights-chart.export {
  width: 1500px!important;
  height: 600px!important;
}

ul.amcharts-amexport-menu-root {
  top: 0!important;
  left: 0!important;
}

li.amcharts-amexport-item.amcharts-amexport-item-level-0 {
  background-color:transparent;
  color:transparent;
}

li.amcharts-amexport-item.amcharts-amexport-item-level-1 {
  white-space:nowrap;
}

/*
.insights-chart.no-data {
  width: 100%;
  height: 1px;
}*/

</style>
